import { useCallback, useMemo } from 'react';
import { ApolloClient, useApolloClient } from '@apollo/client';
import { QueryListParams } from '#/graphql/types';
import DataStoreOptions from './DataStoreOptions';

export interface Response<T> {
  status: number;
  data?: T;
  errors?: string[];
}

export interface ListResponse<T> extends Response<T[]> {
  totalCount?: number;
}

export const createQueries = <T>(
  client: ApolloClient,
  options: Pick<DataStoreOptions<T>, 'getItem' | 'getItemList' | 'getGroupedItemList'>,
) => {
  const getItem = (key: string) => {
    if (!options.getItem) {
      throw new Error('No getItem query provided!');
    }

    return client.query<{ [q: string]: Response<T> }>({
      query: options.getItem,
      variables: {
        id: key,
      },
    });
  };

  const getItemList = (queryParams: QueryListParams) => {
    if (!options.getItemList) {
      throw new Error('No getItemList query provided!');
    }

    return client.query<{ [q: string]: ListResponse<T> }>({
      query: options.getItemList,
      variables: {
        params: queryParams,
      },
    });
  };

  const getGroupedItemList = (queryParams: QueryListParams) => {
    if (!options.getGroupedItemList) {
      throw new Error('No getGroupedItemList query provided!');
    }

    return client.query<{ [q: string]: ListResponse<T> }>({
      query: options.getGroupedItemList,
      variables: {
        params: queryParams,
      },
    });
  };

  return {
    getItem,
    getItemList,
    getGroupedItemList,
  };
};

const useQueries = <T>(options: DataStoreOptions<T>) => {
  const client = useApolloClient();

  return useMemo(
    () => createQueries(client, options),
    [client, options.getItem, options.getItemList, options.getGroupedItemList],
  );
};

export type Queries<T> = ReturnType<typeof useQueries<T>>;

export default useQueries;
